import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import ImmersiveLayout from "../components/ImmersiveLayout";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Immersive HomePage — accessed via "Learn More" on Landing section 1.
 * Editorial dark scrollytelling of Seikat experience + featured products.
 */
export default function HomePage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [collections, setCollections] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products?featured=true&limit=4`).then(r => r.data?.products || []).catch(() => []),
      axios.get(`${API}/collections`).then(r => r.data?.collections || []).catch(() => []),
      axios.get(`${API}/cms/site-settings`).then(r => r.data).catch(() => null)
    ]).then(([fp, cl, st]) => {
      setFeatured(fp);
      setCollections(cl.slice(0, 3));
      setSettings(st);
    });
  }, []);

  const id = language === "id";

  return (
    <ImmersiveLayout settings={settings}>
      {/* HERO — editorial introduction */}
      <section className="relative min-h-[100vh] flex items-center pt-24 pb-32 px-6 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1647644520784-7b6ad81c9efd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-seikat-ink/65" />
          <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink via-seikat-ink/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 space-y-8"
          >
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">{id ? "Selamat Datang" : "Welcome"}</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-seikat-bone leading-[0.95] tracking-tight" data-testid="home-hero-title">
              {id ? <>Setiap <em>bunga</em> menyimpan <em>sebuah cerita</em>.</> : <>Every <em>flower</em> holds a <em>story</em>.</>}
            </h1>
            <p className="font-body text-seikat-bone/75 text-lg max-w-xl font-light leading-relaxed">
              {id
                ? "Seikat adalah surat cinta yang dirangkai dengan tangan — dari kebun pagi hingga ke pintu rumahmu."
                : "Seikat is a love letter, hand-woven — from morning gardens to your doorstep."}
            </p>
            <div className="flex flex-wrap gap-3 pt-4">
              <button onClick={() => navigate("/catalog")} className="btn-pill btn-pill--solid" data-testid="home-cta-catalog" data-magnet>
                {id ? "Jelajahi Bouquet" : "Explore Bouquets"} <ArrowRight size={14} />
              </button>
              <button onClick={() => navigate("/memory-bouquet/create")} className="btn-pill" data-testid="home-cta-memory" data-magnet>
                {id ? "Memory Bouquet" : "Memory Bouquet"} <ArrowRight size={14} />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS — vertical scrollytelling */}
      <section className="relative py-32 px-6 lg:px-16 bg-seikat-ink">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-14">
            <span className="w-8 h-px bg-seikat-lime" />
            <span className="eyebrow-immersive">{id ? "Proses Kami" : "Our Process"}</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-seikat-bone max-w-3xl leading-[1.05] mb-20">
            {id ? <>Dari <em>cerita</em>, menjadi <em>kelopak</em>.</> : <>From <em>story</em> to <em>petal</em>.</>}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { n: "01", id: "Berbagi cerita", en: "Share the story", desc_id: "Ceritakan momen dan perasaanmu kepada Memory Curator.", desc_en: "Tell our Memory Curator the moment and feeling." },
              { n: "02", id: "AI merangkai", en: "AI weaves", desc_id: "Gemini menerjemahkan ceritamu menjadi pilihan bunga ideal.", desc_en: "Gemini translates your story into the ideal bloom selection." },
              { n: "03", id: "Florist menyusun", en: "Florists craft", desc_id: "Florist berpengalaman merangkai dengan tangan dan jiwa.", desc_en: "Experienced florists arrange by hand and heart." },
              { n: "04", id: "Diantarkan", en: "Delivered", desc_id: "Sampai ke pintu mereka pada hari yang sama.", desc_en: "Delivered to their door, the very same day." }
            ].map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative pb-8 border-b border-white/10"
                data-testid={`home-step-${step.n}`}
              >
                <div className="font-heading text-6xl text-seikat-pink/60 leading-none mb-6">{step.n}</div>
                <h3 className="font-heading text-2xl text-seikat-bone mb-3">{id ? step.id : step.en}</h3>
                <p className="font-body text-seikat-bone/65 text-sm leading-relaxed">{id ? step.desc_id : step.desc_en}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS — editorial asymmetric grid */}
      {featured.length > 0 && (
        <section className="relative py-32 px-6 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-14">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-px bg-seikat-pink" />
                  <span className="eyebrow-immersive">{id ? "Pilihan Kurator" : "Curator's Picks"}</span>
                </div>
                <h2 className="font-heading text-4xl sm:text-5xl text-seikat-bone leading-tight max-w-2xl">
                  {id ? <>Rangkaian yang sedang <em>dipuja</em> minggu ini.</> : <>Arrangements <em>adored</em> this week.</>}
                </h2>
              </div>
              <button onClick={() => navigate("/catalog")} className="hidden md:inline-flex btn-pill" data-magnet data-testid="home-view-all">
                {id ? "Lihat Semua" : "View All"} <ArrowRight size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {featured.map((p, i) => (
                <motion.div
                  key={p.product_id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className={`group cursor-pointer ${i === 1 ? "sm:translate-y-8" : ""} ${i === 3 ? "lg:translate-y-12" : ""}`}
                  onClick={() => navigate(`/product/${p.product_id}`)}
                  data-testid={`home-featured-${p.product_id}`}
                  data-magnet
                >
                  <div className="relative aspect-[3/4] overflow-hidden rounded-lg">
                    <img src={p.images?.[0] || p.image_url || "https://images.unsplash.com/photo-1551468220-0a25172193f9?w=800"} alt={id ? p.name_id : p.name_en} className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink/80 via-transparent to-transparent" />
                  </div>
                  <div className="pt-5 pb-2">
                    <p className="eyebrow-immersive mb-2">{p.occasion_tags?.[0] || p.style_tags?.[0] || p.occasion?.[0] || "Signature"}</p>
                    <h3 className="font-heading text-2xl text-seikat-bone mb-1 group-hover:text-seikat-pink transition-colors">{id ? p.name_id : p.name_en}</h3>
                    <p className="font-body text-seikat-bone/65 text-sm">Rp {p.price?.toLocaleString("id-ID")}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTE BLOCK */}
      <section className="relative py-32 px-6 lg:px-16">
        <div className="max-w-5xl mx-auto text-center">
          <motion.blockquote
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl text-seikat-bone leading-[1.2] italic"
          >
            {id
              ? <>"Bunga tidak butuh kata — ia hanya butuh <em className="not-italic text-seikat-pink">dirangkai dengan jujur</em>."</>
              : <>"Flowers don't need words — they only need to be <em className="not-italic text-seikat-pink">arranged with honesty</em>."</>}
          </motion.blockquote>
          <p className="font-body text-seikat-bone-muted text-xs tracking-[0.3em] uppercase mt-8">— SEIKAT ATELIER</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 px-6 lg:px-16">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-seikat-bone leading-tight">
            {id ? <>Ada momen yang ingin <em>diabadikan</em>?</> : <>A moment worth <em>preserving</em>?</>}
          </h2>
          <p className="font-body text-seikat-bone/70 text-base font-light">
            {id ? "Mari kami rangkai untukmu." : "Let us weave it for you."}
          </p>
          <div className="pt-2 flex justify-center">
            <button onClick={() => navigate("/memory-bouquet/create")} className="btn-pill btn-pill--solid" data-magnet data-testid="home-final-cta">
              {id ? "Mulai Memory Bouquet" : "Begin Your Memory Bouquet"} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER strip */}
      <footer className="border-t border-white/10 mt-16 py-12 px-6 lg:px-16 pb-32">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <div className="font-heading text-3xl text-seikat-bone">Seikat<span className="text-seikat-pink">.</span></div>
          <div className="font-body text-xs tracking-widest text-seikat-bone-muted uppercase">© 2026 · Jakarta, Indonesia</div>
        </div>
      </footer>
    </ImmersiveLayout>
  );
}
